/**
 * The poster hung in a photographed room, at scale : the photo covers the scene in height, the
 * frame sits at the room's spot (cx / by, % of the photo) and measures pxPerCm photo pixels per cm,
 * so 42 × 59 cm is 42 × 59 cm against the sofa. Everything is plain HTML and CSS, nothing in WebGL
 * (too heavy for some computers) : the depth comes from CSS 3D transforms only, which the browser
 * composites on the graphics card without drawing anything again.
 *
 * The frame turns on itself : its face (the moulding, a coloured border with a bevel, the print
 * sunk in it, the glass's glare) and four sides for its thickness ; its shadow is a layer of its
 * own that slides, never a box-shadow redrawn. The room is the photo, and does not move.
 *
 * Dragging anywhere on the scene turns the frame ; let go, it comes back. On desktop it also leans
 * a little towards the pointer, and it turns once on its own the first time. Nothing moves for
 * whoever asked for less motion (prefers-reduced-motion).
 *
 * Two layouts (design handoff "Aperçu 3D", desktop.dc.html / mobile.dc.html) :
 *   - desktop : the photo centred in the scene ; "De près" scales it 1.8 around the frame
 *   - mobile  : the photo shifted so that the frame is always centred ; "De près" fits the frame in
 *     the band between the top buttons and the sheet (options.band()), never under either
 *
 * The dimensions are drawn in screen space, outside the zoomed photo : their labels keep their
 * size whatever the zoom.
 *
 *   setRoom(room)            { image, cx, by, pxPerCm, alt } ; answers a Promise resolved once
 *                            its photo is loaded, so that the owner can keep a loader over the
 *                            switch (WallPreviewPopup)
 *   setProduct(product)      { width, height, border, finish } in cm
 *   setArtwork(url)          the poster's picture ; answers a Promise resolved once it is loaded
 *   setView(view)            "face" | "close"
 *   measure(on)
 *   layout()                 after the scene changed size
 *   destroy()
 */
fx.ns("shared.preview3d", class RoomPreview {

    static get PHOTO_WIDTH() { return 1408; }
    static get PHOTO_HEIGHT() { return 768; }
    static get CLOSE_SCALE() { return 1.8; }
    static get ROOM_TIMEOUT() { return 8000; }     // a room never keeps its owner's loader longer
    static get MAX_TURN() { return 22; }            // degrees, the frame at the end of a drag
    static get LEAN() { return 0.3; }               // desktop : how far the pointer leans it, of MAX_TURN
    static get DEPTH() { return { framed : 2.5, poster : 0.3 }; }   // cm, the sides' depth

    /**
     * Where the frame's bottom sits, % of the photo's height. A room with `cy` hangs every size
     * with its centre there, its bottom never lower than `by` : a small poster then hangs as high
     * as a large one is centred, instead of sitting on the sofa ; one too large comes down to `by`
     * and no further.
     */
    // #RRGGBB darkened by `factor` (0 … 1)
    static shade(hex, factor)
    {
        let n = parseInt(String(hex).replace("#", ""), 16);
        if (isNaN(n))
            return hex;
        let c = (shift) => Math.round(((n >> shift) & 255) * factor);
        return "rgb(" + c(16) + ", " + c(8) + ", " + c(0) + ")";
    }

    static bottomOf(room, frameHeightCm)
    {
        if (room.cy === undefined)
            return room.by;

        let half = frameHeightCm * room.pxPerCm / RoomPreview.PHOTO_HEIGHT * 100 / 2;
        return Math.min(room.by, room.cy + half);
    }

    constructor(scene, options)
    {
        this.scene = scene;
        this.options = Object.assign({
            mobile : false,
            band : null,                                        // mobile : () => ({ top, bottom }), scene px
            colors : { wood : "#D6B585", black : "#1E1F21", white : "#F8F7F4" },
            format : (cm) => (Math.round(cm * 10) / 10).toLocaleString(document.documentElement.lang || "fr") + " cm",
        }, options || {});

        this.view = "face";
        this._measuring = false;

        scene.classList.add("roomPreview");
        scene.insertAdjacentHTML("beforeend",
            '<div class="rpStage">' +
                '<img class="rpRoom" alt="" draggable="false">' +
                '<div class="rpFrameWrap">' +
                    '<div class="rpShadow"></div>' +
                    '<div class="rpFrame">' +
                        '<img class="rpArt" alt="" draggable="false">' +
                        '<span class="rpGlare"><i></i></span>' +
                        '<i class="rpSide rpTop"></i><i class="rpSide rpRight"></i><i class="rpSide rpBottom"></i><i class="rpSide rpLeft"></i>' +
                    '</div>' +
                '</div>' +
            '</div>' +
            '<div class="rpMeasure">' +
                '<div class="rpWidth"><i></i><span></span><i></i></div>' +
                '<div class="rpHeight"><i></i><span></span><i></i></div>' +
            '</div>');

        this.stage = scene.querySelector(".rpStage");
        this.photo = scene.querySelector(".rpRoom");
        this.wrap = scene.querySelector(".rpFrameWrap");
        this.frame = scene.querySelector(".rpFrame");
        this.art = scene.querySelector(".rpArt");
        this.measures = scene.querySelector(".rpMeasure");
        this.shadow = scene.querySelector(".rpShadow");
        this.glare = scene.querySelector(".rpGlare i");
        this.sides = ["Top", "Right", "Bottom", "Left"].map((side) => scene.querySelector(".rp" + side));

        this._angle = { x : 0, y : 0 };
        this._goal = { x : 0, y : 0 };
        this._still = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

        // the photo first appears in place, the transitions are for the changes that follow
        requestAnimationFrame(() => requestAnimationFrame(() => scene.classList.add("rpAnimated")));

        if (window.ResizeObserver)
        {
            this._observer = new ResizeObserver(() => this.layout());
            this._observer.observe(scene);
        }

        if (!this._still)
            this._listen();
    }

    // ---- public

    setRoom(room)
    {
        let changed = !this.room || this.room.id != room.id;

        // from one room to the next the poster jumps to its spot : no slide across the switch
        if (changed && this.room)
            this.scene.classList.remove("rpAnimated");

        this.room = room;
        if (changed)
            this._ready = this._whenDrawn(room);

        if (this.photo.getAttribute("src") != room.image)
        {
            this.scene.classList.add("rpLoading");
            this.photo.onload = this.photo.onerror = () => this.scene.classList.remove("rpLoading");
            this.photo.src = room.image;
        }

        this._whenPhoto().then(() => this._roomDrawn(room));

        this.photo.alt = room.alt || "";
        this.layout();

        return this._ready || Promise.resolve();
    }

    // the room drawn : its photo loaded (or failed : the loader never stays for good)
    _whenDrawn(room)
    {
        if (this._drawn)
            this._drawn.resolve();

        let drawn = this._drawn = { room : room };
        let promise = new Promise((resolve) => { drawn.resolve = resolve; });
        let timer = setTimeout(() => drawn.resolve(), RoomPreview.ROOM_TIMEOUT);

        return promise.then(() => {
            clearTimeout(timer);
            if (this._drawn == drawn)
                this._drawn = null;
            if (!this._destroyed)
                requestAnimationFrame(() => requestAnimationFrame(() => this.scene.classList.add("rpAnimated")));
        });
    }

    _roomDrawn(room)
    {
        if (this._drawn && this._drawn.room == room)
            this._drawn.resolve();
    }

    // the flat photo of the current room, loaded
    _whenPhoto()
    {
        if (!this.scene.classList.contains("rpLoading"))
            return Promise.resolve();

        return new Promise((resolve) => {
            this.photo.addEventListener("load", resolve, { once : true });
            this.photo.addEventListener("error", resolve, { once : true });
        });
    }

    setProduct(product)
    {
        this.product = product;
        this.scene.classList.toggle("rpFramed", product.border > 0);
        this.layout();
    }

    setArtwork(url)
    {
        this._artwork = url;

        return new Promise((resolve) => {
            this.art.onload = this.art.onerror = () => resolve();
            this.art.src = url;
        }).then(() => this._introduce());
    }

    // ---- moving

    // once, so that nobody takes it for a picture : a turn each way, then still
    _introduce()
    {
        if (this._introduced || this._still || this._destroyed)
            return;
        this._introduced = true;

        this._timers = [[0.7, 0.12, 400], [-0.5, -0.06, 1050], [0, 0, 1700]].map(([x, y, delay]) =>
            setTimeout(() => { if (!this._drag) this._turn(x, y); }, delay));
    }

    // where the view goes (-1 … 1 each way) ; it gets there eased, one frame at a time
    _turn(x, y)
    {
        this._goal = { x : Math.max(-1, Math.min(1, x)), y : Math.max(-1, Math.min(1, y)) };
        if (!this._raf)
            this._raf = requestAnimationFrame(() => this._step());
    }

    _step()
    {
        this._raf = null;
        if (this._destroyed)
            return;

        let a = this._angle, g = this._goal;
        a.x += (g.x - a.x) * 0.14;
        a.y += (g.y - a.y) * 0.14;
        let done = Math.abs(g.x - a.x) < 0.002 && Math.abs(g.y - a.y) < 0.002;
        if (done)
            Object.assign(a, g);

        this._apply();
        if (!done)
            this._raf = requestAnimationFrame(() => this._step());
    }

    // transforms only : nothing is laid out or painted again
    _apply()
    {
        let x = this._angle.x, y = this._angle.y;
        let turn = RoomPreview.MAX_TURN;

        this.frame.style.transform = "rotateY(" + (x * turn).toFixed(2) + "deg) rotateX(" + (-y * turn).toFixed(2) + "deg)";
        // the shadow falls the other way, the glare runs across the glass
        this.shadow.style.transform = "translate(" + (-x * this._shadowShift).toFixed(1) + "px, " + (-y * this._shadowShift * 0.6).toFixed(1) + "px)";
        this.glare.style.transform = "translateX(" + (x * 30).toFixed(1) + "%)";
        this.glare.parentNode.style.opacity = Math.min(1, Math.abs(x) * 2 + Math.abs(y)).toFixed(2);

        // the dimensions are drawn around the frame at rest
        this.scene.classList.toggle("rpMoving", Math.abs(x) > 0.03 || Math.abs(y) > 0.03);
    }

    _listen()
    {
        let scene = this.scene;

        this._handlers = {
            pointerdown : (e) => {
                if (e.target.closest("button, a"))
                    return;
                this._drag = { x : e.clientX, y : e.clientY };
                scene.classList.add("rpDragging");
            },
            pointermove : (e) => {
                if (this._drag)
                {
                    let size = Math.max(280, Math.min(scene.clientWidth, 700));
                    this._turn((e.clientX - this._drag.x) / size * 1.8, (e.clientY - this._drag.y) / size * 1.4);
                }
                else
                if (!this.options.mobile && e.pointerType == "mouse")
                {
                    let rect = scene.getBoundingClientRect();
                    let x = (e.clientX - rect.left) / rect.width - 0.5;
                    let y = (e.clientY - rect.top) / rect.height - 0.5;
                    if (x >= -0.5 && x <= 0.5 && y >= -0.5 && y <= 0.5)
                        this._turn(x * 2 * RoomPreview.LEAN, y * 2 * RoomPreview.LEAN * 0.6);
                }
            },
            pointerup : () => {
                if (!this._drag)
                    return;
                this._drag = null;
                scene.classList.remove("rpDragging");
                this._turn(0, 0);
            },
            pointerleave : (e) => {
                if (!this._drag && e.pointerType == "mouse")
                    this._turn(0, 0);
            },
        };

        scene.addEventListener("pointerdown", this._handlers.pointerdown);
        scene.addEventListener("pointerleave", this._handlers.pointerleave);
        window.addEventListener("pointermove", this._handlers.pointermove);
        window.addEventListener("pointerup", this._handlers.pointerup);
        window.addEventListener("pointercancel", this._handlers.pointerup);
    }

    setView(view)
    {
        this.view = view == "close" ? "close" : "face";
        this.layout();
    }

    measure(on)
    {
        this._measuring = !!on;
        this.scene.classList.toggle("rpMeasuring", this._measuring);
    }

    destroy()
    {
        this._destroyed = true;

        if (this._observer)
            this._observer.disconnect();

        if (this._drawn)
            this._drawn.resolve();

        (this._timers || []).forEach(clearTimeout);
        if (this._raf)
            cancelAnimationFrame(this._raf);

        if (this._handlers)
        {
            this.scene.removeEventListener("pointerdown", this._handlers.pointerdown);
            this.scene.removeEventListener("pointerleave", this._handlers.pointerleave);
            window.removeEventListener("pointermove", this._handlers.pointermove);
            window.removeEventListener("pointerup", this._handlers.pointerup);
            window.removeEventListener("pointercancel", this._handlers.pointerup);
        }
    }

    /**
     * Everything follows from the scene's size : the photo's (cover, in height), the scale, the
     * frame's place, the zoom, and the dimensions around the frame's rectangle on screen.
     */
    layout()
    {
        if (!this.room || !this.product || this._destroyed)
            return;

        let sw = this.scene.clientWidth;
        let sh = this.scene.clientHeight;
        if (!sw || !sh)
            return;

        let room = this.room;
        let product = this.product;
        let PW = RoomPreview.PHOTO_WIDTH, PH = RoomPreview.PHOTO_HEIGHT;

        let H = Math.max(sh, sw * PH / PW);
        let W = PW * H / PH;
        let k = room.pxPerCm * H / PH;                         // screen px per cm

        let border = product.border || 0;
        let fw = (product.width + 2 * border) * k;
        let fh = (product.height + 2 * border) * k;

        // the frame's centre, in the photo
        let fcx = room.cx / 100 * W;
        let by = RoomPreview.bottomOf(room, product.height + 2 * border);
        let fcy = by / 100 * H - fh / 2;

        let left = this.options.mobile ? sw / 2 - fcx : (sw - W) / 2;
        let top = (sh - H) / 2;
        let scale = 1;
        let dy = 0;

        if (this.options.mobile)
        {
            let band = this.options.band ? this.options.band() : { top : 0, bottom : sh };
            let bandHeight = band.bottom - band.top;

            if (this.view == "close")
            {
                // room for the width label above and the height label on the right
                scale = Math.min((bandHeight - 40) * 0.9 / fh, (sw - 150) / fw);
                dy = band.top + 14 + bandHeight / 2 - (top + fcy);
            }
            else
            {
                // never under the buttons nor the sheet
                let frameTop = top + fcy - fh / 2;
                let frameBottom = top + fcy + fh / 2;
                if (frameTop < band.top + 34)
                    dy = band.top + 34 - frameTop;
                else
                if (frameBottom > band.bottom - 12)
                    dy = Math.max(band.top + 34 - frameTop, band.bottom - 12 - frameBottom);
            }
        }
        else
        if (this.view == "close")
        {
            // close up, the whole frame in sight with its dimensions around it : brought to the
            // middle of the scene (scaled around its own centre, a frame hung high — a 59 × 84 —
            // would leave it at the top), as far as the photo still covers the scene
            scale = Math.max(1, Math.min(RoomPreview.CLOSE_SCALE, (sh - 90) / fh, (sw - 160) / fw));
            let centre = sh / 2;
            centre = Math.min(centre, fcy * scale);                          // the photo's top stays above the scene
            centre = Math.max(centre, sh - (H - fcy) * scale);              // and its bottom below
            dy = centre - (top + fcy);
        }

        Object.assign(this.stage.style, {
            width : W + "px",
            height : H + "px",
            left : left + "px",
            top : top + "px",
            transformOrigin : fcx + "px " + fcy + "px",
            transform : "translateY(" + dy + "px) scale(" + scale + ")",
        });

        let colour = border ? this.options.colors[product.finish] || "#FFFFFF" : "#FFFFFF";
        Object.assign(this.wrap.style, { left : room.cx + "%", top : by + "%", perspective : Math.round(Math.max(fw, fh) * 4) + "px" });
        Object.assign(this.frame.style, {
            width : fw + "px",
            height : fh + "px",
            padding : border * k + "px",
            backgroundColor : border ? colour : "transparent",
        });
        Object.assign(this.shadow.style, { width : fw + "px", height : fh + "px" });
        Object.assign(this.glare.parentNode.style, { top : border * k + "px", right : border * k + "px", bottom : border * k + "px", left : border * k + "px" });

        // the sides : as deep as the moulding (a poster alone barely), in its colour a little darker
        let depth = Math.max(1, (border ? RoomPreview.DEPTH.framed : RoomPreview.DEPTH.poster) * k);
        let side = RoomPreview.shade(border ? colour : "#E6E6E6", border ? 0.78 : 0.92);
        this.sides.forEach((element, i) => {
            let across = i % 2 == 0;                        // top and bottom run across, the others down
            Object.assign(element.style, { width : across ? "" : depth + "px", height : across ? depth + "px" : "", backgroundColor : side });
        });

        this._shadowShift = Math.max(4, Math.min(fw, fh) * 0.08);
        this._apply();

        this.measures.querySelector(".rpWidth span").textContent = this.options.format(product.width + 2 * border);
        this.measures.querySelector(".rpHeight span").textContent = this.options.format(product.height + 2 * border);

        // the frame on screen
        let cx = left + fcx;
        let cy = top + fcy + dy;
        Object.assign(this.measures.style, {
            left : cx - fw * scale / 2 + "px",
            top : cy - fh * scale / 2 + "px",
            width : fw * scale + "px",
            height : fh * scale + "px",
        });
    }
});
